import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DnsProviderDto,
  DnsProviderType,
  DnsRecordData,
  DnsRecordDto,
  DnsRecordType,
  DnsSyncStatus,
  DnsTemplateDto,
  DnsTemplateSyncResultDto,
  DnsZoneDetailDto,
  DnsZoneDnssecDto,
  DnsZoneDto,
  DnsZoneSyncDto,
  SubscriptionDnsDto,
} from '@cpm/shared';
import { DNS_DEFAULT_TTL, DNS_RECORD_TYPES, normalizeDnsOwner } from '@cpm/shared';
import { ChevronDown, ChevronRight, Globe, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { timeAgo } from './charts.js';
import { Modal } from './Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  Select,
  Spinner,
  Textarea,
  type BadgeTone,
} from './ui.js';

/**
 * DNS panels (G12, ADR-0020/0021/0022): per-subscription zone management —
 * records, DNSSEC and provider replication — materialized on the panel's
 * PowerDNS hidden primary.
 */

export const PROVIDER_TYPE_LABELS: Record<DnsProviderType, string> = {
  cloudflare: 'Cloudflare',
  route53: 'Route 53',
  azure: 'Azure DNS',
  digitalocean: 'DigitalOcean',
  axfr: 'AXFR secondary',
};

const SYNC_TONES: Record<DnsSyncStatus, BadgeTone> = {
  pending: 'gray',
  in_sync: 'green',
  drift: 'yellow',
  error: 'red',
};

// ── Forms ───────────────────────────────────────────────────────────

export function ZoneForm({
  subscriptionId,
  assignment,
  onCreated,
  onDone,
}: {
  subscriptionId: string;
  assignment?: { id: string; name: string };
  onCreated?: (id: string) => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const templates = useQuery({
    queryKey: ['dns', 'templates'],
    queryFn: () => api.get<DnsTemplateDto[]>('/api/dns/templates'),
  });
  const [name, setName] = useState(assignment?.name ?? '');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [ip, setIp] = useState('');
  const selectedTemplate =
    templateId ?? templates.data?.find((t) => t.isDefault)?.id ?? templates.data?.[0]?.id ?? '';

  const save = useMutation({
    mutationFn: () =>
      api.post<{ id?: string; zoneId?: string }>(
        assignment
          ? `/api/dns/domains/${assignment.id}/zone`
          : `/api/dns/subscriptions/${subscriptionId}/zones`,
        {
          name,
          ...(selectedTemplate !== '' ? { templateId: selectedTemplate } : {}),
          ...(ip.trim() !== '' ? { ip: ip.trim() } : {}),
        },
      ),
    onSuccess: (zone) => {
      void queryClient.invalidateQueries({ queryKey: ['dns'] });
      const id = zone.zoneId ?? zone.id;
      if (id) onCreated?.(id);
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <Label htmlFor="zone-name">Zone name</Label>
        <Input
          id="zone-name"
          required
          readOnly={!!assignment}
          placeholder="example.com"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="zone-template">Template</Label>
        <Select
          id="zone-template"
          value={selectedTemplate}
          onChange={(e) => setTemplateId(e.target.value)}
        >
          {(templates.data ?? []).map((template) => (
            <option key={template.id} value={template.id}>
              {template.name}
              {template.isDefault ? ' (default)' : ''}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <Label htmlFor="zone-ip">Zone IP (optional)</Label>
        <Input
          id="zone-ip"
          placeholder="203.0.113.10"
          value={ip}
          onChange={(e) => setIp(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          IPv4 the template's <span className="font-mono">&lt;ip&gt;</span> expands to; empty uses
          the module default.
        </p>
      </div>
      <p className="text-xs text-slate-500">
        New zones await provider setup of public publishing. Registrar delegation remains a separate
        step.
      </p>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          Add zone
        </Button>
      </div>
    </form>
  );
}

function ZoneIpForm({ zone, onDone }: { zone: DnsZoneDto; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [ip, setIp] = useState(zone.ip ?? '');
  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/dns/zones/${zone.id}`, {
        ip: ip.trim() === '' ? null : ip.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dns'] });
      onDone();
    },
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div>
        <Label htmlFor="zone-edit-ip">Zone IP (empty = module default)</Label>
        <Input
          id="zone-edit-ip"
          placeholder="203.0.113.10"
          value={ip}
          onChange={(e) => setIp(e.target.value)}
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Applied to template records on the next template re-sync.
        </p>
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          Save
        </Button>
      </div>
    </form>
  );
}

export function RecordForm({
  zoneId,
  zoneName,
  expectedRevision,
  existing,
  onDone,
}: {
  zoneId: string;
  zoneName?: string;
  expectedRevision?: number;
  existing?: DnsRecordDto;
  onDone: () => void;
}) {
  const [editingRevision] = useState(expectedRevision);
  const queryClient = useQueryClient();
  const data = existing?.data;
  const [name, setName] = useState(existing && existing.name !== '@' ? `${existing.fqdn}.` : '@');
  const [moreTypes, setMoreTypes] = useState(
    !!existing && !['A', 'AAAA', 'CNAME', 'MX', 'TXT'].includes(existing.type),
  );
  const [ttl, setTtl] = useState(String(existing?.ttl ?? DNS_DEFAULT_TTL));
  const [type, setType] = useState<DnsRecordType>(existing?.type ?? 'A');
  const [address, setAddress] = useState(
    data && (data.type === 'A' || data.type === 'AAAA') ? data.address : '',
  );
  const [target, setTarget] = useState(data && 'target' in data ? data.target : '');
  const [priority, setPriority] = useState(
    data && 'priority' in data ? String(data.priority) : '10',
  );
  const [weight, setWeight] = useState(data && data.type === 'SRV' ? String(data.weight) : '0');
  const [port, setPort] = useState(data && data.type === 'SRV' ? String(data.port) : '');
  const [text, setText] = useState(
    data && (data.type === 'TXT' || data.type === 'SPF') ? data.text : '',
  );
  const [flags, setFlags] = useState(data && data.type === 'CAA' ? String(data.flags) : '0');
  const [tag, setTag] = useState<'issue' | 'issuewild' | 'iodef'>(
    data && data.type === 'CAA' ? data.tag : 'issue',
  );
  const [value, setValue] = useState(data && data.type === 'CAA' ? data.value : '');
  const [usage, setUsage] = useState(data && data.type === 'TLSA' ? String(data.usage) : '3');
  const [selector, setSelector] = useState(
    data && data.type === 'TLSA' ? String(data.selector) : '1',
  );
  const [matchingType, setMatchingType] = useState(
    data && data.type === 'TLSA' ? String(data.matchingType) : '1',
  );
  const [associationData, setAssociationData] = useState(
    data && data.type === 'TLSA' ? data.certificateAssociationData : '',
  );
  const [keyTag, setKeyTag] = useState(data && data.type === 'DS' ? String(data.keyTag) : '');
  const [algorithm, setAlgorithm] = useState(
    data && data.type === 'DS' ? String(data.algorithm) : '13',
  );
  const [digestType, setDigestType] = useState(
    data && data.type === 'DS' ? String(data.digestType) : '2',
  );
  const [digest, setDigest] = useState(data && data.type === 'DS' ? data.digest : '');

  function buildData(): DnsRecordData {
    switch (type) {
      case 'A':
        return { type, address };
      case 'AAAA':
        return { type, address };
      case 'CNAME':
        return { type, target };
      case 'NS':
        return { type, target };
      case 'PTR':
        return { type, target };
      case 'MX':
        return { type, priority: Number(priority), target };
      case 'TXT':
        return { type, text };
      case 'SPF':
        return { type, text };
      case 'SRV':
        return {
          type,
          priority: Number(priority),
          weight: Number(weight),
          port: Number(port),
          target,
        };
      case 'CAA':
        return { type, flags: Number(flags), tag, value };
      case 'TLSA':
        return {
          type,
          usage: Number(usage),
          selector: Number(selector),
          matchingType: Number(matchingType),
          certificateAssociationData: associationData,
        };
      case 'DS':
        return {
          type,
          keyTag: Number(keyTag),
          algorithm: Number(algorithm),
          digestType: Number(digestType),
          digest,
        };
    }
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        ttl: Number(ttl),
        data: buildData(),
        expectedRevision: editingRevision,
      };
      return existing
        ? api.patch(`/api/dns/records/${existing.id}`, body)
        : api.post(`/api/dns/zones/${zoneId}/records`, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dns'] });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="rec-name">Name</Label>
          <Input
            id="rec-name"
            required
            className="font-mono text-xs"
            placeholder="@"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Use @ for the domain itself, a relative name, or a full hostname within this domain.{' '}
            {zoneName && (
              <span className="block">
                Result:{' '}
                {(() => {
                  try {
                    const owner = normalizeDnsOwner(name, zoneName);
                    return owner === '@' ? zoneName : `${owner}.${zoneName}`;
                  } catch {
                    return 'Enter a hostname within this domain';
                  }
                })()}
              </span>
            )}
          </p>
        </div>
        <div>
          <Label htmlFor="rec-type">Type</Label>
          <Select
            id="rec-type"
            value={type}
            onChange={(e) => setType(e.target.value as DnsRecordType)}
          >
            {DNS_RECORD_TYPES.filter(
              (t) => moreTypes || ['A', 'AAAA', 'CNAME', 'MX', 'TXT'].includes(t),
            ).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setMoreTypes(true)}
            disabled={moreTypes}
          >
            More record types
          </Button>
        </div>
      </div>
      {(type === 'A' || type === 'AAAA') && (
        <div>
          <Label htmlFor="rec-address">{type === 'A' ? 'IPv4 address' : 'IPv6 address'}</Label>
          <Input
            id="rec-address"
            required
            className="font-mono text-xs"
            placeholder={type === 'A' ? '203.0.113.10' : '2001:db8::1'}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>
      )}
      {(type === 'CNAME' || type === 'NS' || type === 'PTR') && (
        <div>
          <Label htmlFor="rec-target">
            {type === 'CNAME'
              ? 'Target hostname'
              : type === 'NS'
                ? 'Nameserver hostname'
                : 'Pointer target hostname'}
          </Label>
          <Input
            id="rec-target"
            required
            className="font-mono text-xs"
            placeholder={type === 'CNAME' ? 'other.example.com' : 'ns1.example.com'}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
        </div>
      )}
      {type === 'MX' && (
        <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
          <div>
            <Label htmlFor="rec-priority">Priority</Label>
            <Input
              id="rec-priority"
              required
              type="number"
              min={0}
              max={65_535}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="rec-target">Mail server</Label>
            <Input
              id="rec-target"
              required
              className="font-mono text-xs"
              placeholder="mail.example.com"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              For a domain that receives no email, use . with priority 0 (Null MX). It must be the
              only MX. To enable mail later, prepare mailboxes and have your provider activate mail
              DNS.
            </p>
          </div>
        </div>
      )}
      {(type === 'TXT' || type === 'SPF') && (
        <div>
          <Label htmlFor="rec-text">Text</Label>
          <Textarea
            id="rec-text"
            required
            rows={3}
            className="font-mono text-xs"
            placeholder={type === 'SPF' ? 'v=spf1 mx ~all' : undefined}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      )}
      {type === 'SRV' && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="rec-priority">Priority</Label>
              <Input
                id="rec-priority"
                required
                type="number"
                min={0}
                max={65_535}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="rec-weight">Weight</Label>
              <Input
                id="rec-weight"
                required
                type="number"
                min={0}
                max={65_535}
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="rec-port">Port</Label>
              <Input
                id="rec-port"
                required
                type="number"
                min={0}
                max={65_535}
                value={port}
                onChange={(e) => setPort(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="rec-target">Target hostname</Label>
            <Input
              id="rec-target"
              required
              className="font-mono text-xs"
              placeholder="sip.example.com"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
          </div>
        </>
      )}
      {type === 'CAA' && (
        <div className="grid gap-4 sm:grid-cols-[6rem_9rem_1fr]">
          <div>
            <Label htmlFor="rec-flags">Flags</Label>
            <Input
              id="rec-flags"
              required
              type="number"
              min={0}
              max={255}
              value={flags}
              onChange={(e) => setFlags(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="rec-tag">Tag</Label>
            <Select
              id="rec-tag"
              value={tag}
              onChange={(e) => setTag(e.target.value as 'issue' | 'issuewild' | 'iodef')}
            >
              <option value="issue">issue</option>
              <option value="issuewild">issuewild</option>
              <option value="iodef">iodef</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="rec-value">Value</Label>
            <Input
              id="rec-value"
              required
              className="font-mono text-xs"
              placeholder="letsencrypt.org"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
        </div>
      )}
      {type === 'TLSA' && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="rec-tlsa-usage">Certificate usage</Label>
              <Input
                id="rec-tlsa-usage"
                required
                type="number"
                min={0}
                max={3}
                value={usage}
                onChange={(e) => setUsage(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="rec-tlsa-selector">Selector</Label>
              <Input
                id="rec-tlsa-selector"
                required
                type="number"
                min={0}
                max={1}
                value={selector}
                onChange={(e) => setSelector(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="rec-tlsa-match">Matching type</Label>
              <Input
                id="rec-tlsa-match"
                required
                type="number"
                min={0}
                max={2}
                value={matchingType}
                onChange={(e) => setMatchingType(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="rec-tlsa-data">Certificate association data (hex)</Label>
            <Textarea
              id="rec-tlsa-data"
              required
              rows={3}
              className="font-mono text-xs uppercase"
              value={associationData}
              onChange={(e) => setAssociationData(e.target.value)}
            />
          </div>
        </>
      )}
      {type === 'DS' && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="rec-ds-keytag">Key tag</Label>
              <Input
                id="rec-ds-keytag"
                required
                type="number"
                min={0}
                max={65_535}
                value={keyTag}
                onChange={(e) => setKeyTag(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="rec-ds-algorithm">Algorithm</Label>
              <Input
                id="rec-ds-algorithm"
                required
                type="number"
                min={0}
                max={255}
                value={algorithm}
                onChange={(e) => setAlgorithm(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="rec-ds-digest-type">Digest type</Label>
              <Input
                id="rec-ds-digest-type"
                required
                type="number"
                min={0}
                max={255}
                value={digestType}
                onChange={(e) => setDigestType(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="rec-ds-digest">Digest (hex)</Label>
            <Textarea
              id="rec-ds-digest"
              required
              rows={3}
              className="font-mono text-xs uppercase"
              value={digest}
              onChange={(e) => setDigest(e.target.value)}
            />
          </div>
        </>
      )}
      <div>
        <Label htmlFor="rec-ttl-preset">TTL</Label>
        <Select
          id="rec-ttl-preset"
          value={['60', '300', '1800', '3600', '86400'].includes(ttl) ? ttl : 'custom'}
          onChange={(e) => {
            if (e.target.value !== 'custom') setTtl(e.target.value);
            else setTtl('');
          }}
        >
          <option value="60">1 minute</option>
          <option value="300">5 minutes</option>
          <option value="1800">30 minutes</option>
          <option value="3600">1 hour (default)</option>
          <option value="86400">1 day</option>
          <option value="custom">Custom</option>
        </Select>
        <Label htmlFor="rec-ttl">TTL (seconds)</Label>
        <Input
          id="rec-ttl"
          required
          type="number"
          min={60}
          max={604_800}
          value={ttl}
          onChange={(e) => setTtl(e.target.value)}
        />
      </div>
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {existing ? 'Save changes' : 'Add record'}
        </Button>
      </div>
    </form>
  );
}

// ── Zone detail blocks ──────────────────────────────────────────────

export function RecordsTable({
  detail,
  featureEnabled,
  onEdit,
  onDelete,
}: {
  detail: Pick<DnsZoneDetailDto, 'records'>;
  featureEnabled: boolean;
  onEdit: (record: DnsRecordDto) => void;
  onDelete: (record: DnsRecordDto) => void;
}) {
  if (detail.records.length === 0) {
    return <p className="px-5 py-3 text-sm text-slate-500 dark:text-slate-400">No records yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
          <tr>
            <th className="px-5 py-3 font-medium">Host</th>
            <th className="px-5 py-3 font-medium">Type</th>
            <th className="px-5 py-3 font-medium">Value</th>
            <th className="px-5 py-3 font-medium">TTL</th>
            <th className="px-5 py-3 font-medium">Origin</th>
            <th className="px-5 py-3" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {detail.records.map((record) => (
            <tr key={record.id}>
              <td className="px-5 py-3 font-mono text-xs font-medium">{record.fqdn}</td>
              <td className="px-5 py-3 text-xs">{record.type}</td>
              <td className="max-w-sm truncate px-5 py-3 font-mono text-xs text-slate-600 dark:text-slate-300">
                {record.content}
              </td>
              <td className="px-5 py-3 text-xs tabular-nums">{record.ttl}</td>
              <td className="px-5 py-3">
                {record.origin === 'auto' && <Badge tone="gray">auto</Badge>}
                {record.origin === 'template' && <Badge tone="blue">template</Badge>}
              </td>
              <td className="whitespace-nowrap px-5 py-3 text-right">
                {featureEnabled && record.origin !== 'auto' && (
                  <>
                    <Button
                      variant="ghost"
                      aria-label={`Edit record ${record.fqdn} ${record.type}`}
                      onClick={() => onEdit(record)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      className="hover:!text-red-600"
                      aria-label={`Delete record ${record.fqdn} ${record.type}`}
                      onClick={() => onDelete(record)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DnssecBlock({ zoneId, featureEnabled }: { zoneId: string; featureEnabled: boolean }) {
  const queryClient = useQueryClient();
  const dnssec = useQuery({
    queryKey: ['dns', 'zone', zoneId, 'dnssec'],
    queryFn: () => api.get<DnsZoneDnssecDto>(`/api/dns/zones/${zoneId}/dnssec`),
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.put(`/api/dns/zones/${zoneId}/dnssec`, { enabled }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['dns'] }),
  });

  return (
    <div className="border-t border-slate-100 px-5 py-4 dark:border-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          DNSSEC
          {dnssec.data && (
            <Badge tone={dnssec.data.enabled ? 'green' : 'gray'}>
              {dnssec.data.enabled ? 'enabled' : 'disabled'}
            </Badge>
          )}
        </h4>
        {featureEnabled && dnssec.data && (
          <Button
            variant="secondary"
            loading={toggle.isPending}
            onClick={() => {
              const enabling = !dnssec.data.enabled;
              if (
                window.confirm(
                  enabling
                    ? 'Enable DNSSEC? The zone gets signed and DS records are generated for the registrar.'
                    : 'Disable DNSSEC? Remove the DS records at the registrar first, or the zone goes bogus for validating resolvers.',
                )
              ) {
                toggle.mutate(enabling);
              }
            }}
          >
            {dnssec.data.enabled ? 'Disable DNSSEC' : 'Enable DNSSEC'}
          </Button>
        )}
      </div>
      {dnssec.isPending ? (
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : dnssec.error ? (
        <div className="mt-2">
          <ErrorNote error={dnssec.error} />
        </div>
      ) : (
        <div className="mt-2 space-y-2 text-sm">
          {toggle.error != null && <ErrorNote error={toggle.error} />}
          {dnssec.data.enabled && dnssec.data.dsRecords.length > 0 && (
            <div>
              <pre className="overflow-x-auto rounded-lg bg-slate-950 p-4 font-mono text-xs leading-relaxed text-slate-100">
                {dnssec.data.dsRecords.join('\n')}
              </pre>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Publish these DS records at your registrar to complete the chain of trust.
              </p>
            </div>
          )}
          {dnssec.data.enabled && dnssec.data.unsignedProviders.length > 0 && (
            <p className="rounded-lg bg-amber-50 px-4 py-3 text-amber-700 ring-1 ring-inset ring-amber-600/10 dark:bg-amber-500/10 dark:text-amber-400">
              Zones replicated to {dnssec.data.unsignedProviders.join(', ')} are served unsigned
              there (provider-managed DNSSEC applies).
            </p>
          )}
          {!dnssec.data.enabled && (
            <p className="text-slate-500 dark:text-slate-400">The zone is served unsigned.</p>
          )}
        </div>
      )}
    </div>
  );
}

function ProviderSyncBlock({
  zoneId,
  syncs,
  featureEnabled,
}: {
  zoneId: string;
  syncs: DnsZoneSyncDto[];
  featureEnabled: boolean;
}) {
  const queryClient = useQueryClient();
  const [providerDs, setProviderDs] = useState<Record<string, string>>({});
  const [zoneHandles, setZoneHandles] = useState<Record<string, string>>({});
  const [publisherStopped, setPublisherStopped] = useState<Record<string, boolean>>({});
  const providers = useQuery({
    queryKey: ['dns', 'providers'],
    queryFn: () => api.get<DnsProviderDto[]>('/api/dns/providers'),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['dns'] });
  const prepare = useMutation({
    mutationFn: (providerId: string) =>
      api.post<DnsZoneSyncDto>(`/api/dns/zones/${zoneId}/providers/${providerId}/prepare`, {
        providerZoneId: zoneHandles[providerId]?.trim(),
      }),
    onSuccess: invalidate,
  });
  const publication = useMutation({
    mutationFn: ({ providerId, enabled }: { providerId: string; enabled: boolean }) =>
      api.put(`/api/dns/zones/${zoneId}/providers/${providerId}/publication`, {
        enabled,
        sourcePublisherStopped: publisherStopped[providerId] ?? false,
      }),
    onSuccess: invalidate,
  });
  const toggle = useMutation({
    mutationFn: ({ providerId, enabled }: { providerId: string; enabled: boolean }) =>
      api.put(`/api/dns/zones/${zoneId}/providers/${providerId}`, { enabled }),
    onSuccess: invalidate,
  });
  const run = useMutation({
    mutationFn: ({ providerId, mode }: { providerId: string; mode: 'sync' | 'check' }) =>
      api.post<DnsZoneSyncDto>(`/api/dns/zones/${zoneId}/providers/${providerId}/${mode}`),
    onSuccess: invalidate,
  });
  const configureDnssec = useMutation({
    mutationFn: ({ providerId, dsRecords }: { providerId: string; dsRecords: string[] }) =>
      api.put(`/api/dns/zones/${zoneId}/providers/${providerId}`, {
        enabled: true,
        dnssec: { mode: 'provider-managed', dsRecords },
      }),
    onSuccess: invalidate,
  });

  return (
    <div className="border-t border-slate-100 px-5 py-4 dark:border-slate-800">
      <h4 className="text-sm font-semibold">Provider replication</h4>
      {(toggle.error ?? run.error ?? configureDnssec.error ?? prepare.error ?? publication.error) !=
        null && (
        <div className="mt-2">
          <ErrorNote
            error={
              toggle.error ??
              run.error ??
              configureDnssec.error ??
              prepare.error ??
              publication.error
            }
          />
        </div>
      )}
      {providers.isPending ? (
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : providers.error ? (
        <div className="mt-2">
          <ErrorNote error={providers.error} />
        </div>
      ) : providers.data.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          No sync providers configured yet — an administrator adds them under DNS.
        </p>
      ) : (
        <ul className="mt-1 divide-y divide-slate-100 dark:divide-slate-800">
          {providers.data.map((provider) => {
            const sync = syncs.find((s) => s.providerId === provider.id);
            return (
              <li key={provider.id} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
                      checked={sync !== undefined}
                      disabled={
                        !featureEnabled ||
                        toggle.isPending ||
                        (provider.type === 'route53' && !sync)
                      }
                      onChange={(e) =>
                        toggle.mutate({
                          providerId: provider.id,
                          enabled: e.target.checked,
                        })
                      }
                    />
                    <span className="font-medium">{provider.label}</span>
                    <Badge tone="blue">{PROVIDER_TYPE_LABELS[provider.type]}</Badge>
                  </label>
                  {sync && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge tone={sync.publicationEnabled ? SYNC_TONES[sync.status] : 'gray'}>
                        {sync.publicationEnabled ? sync.status : 'Publication paused'}
                      </Badge>
                      {sync.lastSyncedAt && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          synced {timeAgo(sync.lastSyncedAt)}
                        </span>
                      )}
                      {featureEnabled && (
                        <>
                          <Button
                            variant="ghost"
                            disabled={!sync.publicationEnabled}
                            loading={
                              run.isPending &&
                              run.variables.providerId === provider.id &&
                              run.variables.mode === 'sync'
                            }
                            onClick={() =>
                              run.mutate({
                                providerId: provider.id,
                                mode: 'sync',
                              })
                            }
                          >
                            Sync now
                          </Button>
                          <Button
                            variant="ghost"
                            loading={
                              run.isPending &&
                              run.variables.providerId === provider.id &&
                              run.variables.mode === 'check'
                            }
                            onClick={() =>
                              run.mutate({
                                providerId: provider.id,
                                mode: 'check',
                              })
                            }
                          >
                            Check drift
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {!sync && provider.type === 'route53' && featureEnabled && (
                  <div className="mt-3 space-y-2 text-xs text-slate-500 dark:text-slate-400">
                    <p>
                      Prepare the existing public hosted zone before publication. This reads AWS and
                      leaves automatic sync paused.
                    </p>
                    <Label htmlFor={`zone-handle-${provider.id}`}>Existing hosted-zone ID</Label>
                    <Input
                      id={`zone-handle-${provider.id}`}
                      placeholder="Z0123456789"
                      value={zoneHandles[provider.id] ?? ''}
                      onChange={(event) =>
                        setZoneHandles((current) => ({
                          ...current,
                          [provider.id]: event.target.value,
                        }))
                      }
                    />
                    <Button
                      variant="secondary"
                      loading={prepare.isPending}
                      disabled={!zoneHandles[provider.id]?.trim()}
                      onClick={() => prepare.mutate(provider.id)}
                    >
                      Prepare binding
                    </Button>
                  </div>
                )}
                {sync && featureEnabled && (
                  <div className="mt-3 space-y-2 text-xs text-slate-500 dark:text-slate-400">
                    {sync.providerZoneId && (
                      <p>
                        Hosted zone: <span className="font-mono">{sync.providerZoneId}</span>
                      </p>
                    )}
                    {!sync.publicationEnabled && (
                      <>
                        <p>
                          Check drift and reconcile every record before enabling publication.
                          Pausing keeps records and the binding; a request already in progress may
                          finish.
                        </p>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={publisherStopped[provider.id] ?? false}
                            onChange={(event) =>
                              setPublisherStopped((current) => ({
                                ...current,
                                [provider.id]: event.target.checked,
                              }))
                            }
                          />
                          The previous DNS publisher has stopped for this zone.
                        </label>
                      </>
                    )}
                    <Button
                      variant="secondary"
                      loading={publication.isPending}
                      disabled={
                        !sync.publicationEnabled &&
                        (!publisherStopped[provider.id] || !sync.drift?.inSync)
                      }
                      onClick={() =>
                        publication.mutate({
                          providerId: provider.id,
                          enabled: !sync.publicationEnabled,
                        })
                      }
                    >
                      {sync.publicationEnabled ? 'Pause publication' : 'Enable publication'}
                    </Button>
                  </div>
                )}
                {sync?.lastError && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{sync.lastError}</p>
                )}
                {sync?.drift && !sync.drift.inSync && (
                  <div className="mt-1 space-y-1 text-xs">
                    <p className="text-amber-600 dark:text-amber-400">
                      Drift: {sync.drift.missing} missing · {sync.drift.extra} extra ·{' '}
                      {sync.drift.changed} changed · {sync.drift.unsupported} unsupported · checked{' '}
                      {timeAgo(sync.drift.checkedAt)}
                    </p>
                    {sync.drift.entries.length > 0 && (
                      <ul className="space-y-0.5 font-mono text-slate-500 dark:text-slate-400">
                        {sync.drift.entries.map((entry, i) => (
                          <li key={i}>
                            {entry.kind}: {entry.name} {entry.type} — {entry.detail}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {sync?.drift && sync.drift.skippedUnsupported.length > 0 && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Not supported by this provider (skipped):{' '}
                    {sync.drift.skippedUnsupported.join(', ')}
                  </p>
                )}
                {sync && provider.type === 'axfr' && (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    <Badge tone="blue">panel signatures</Badge> This secondary transfers PowerDNS
                    DNSSEC signatures and uses the panel DS set.
                  </p>
                )}
                {sync && provider.type !== 'axfr' && (
                  <details className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    <summary className="cursor-pointer">
                      {sync.dnssec?.mode === 'provider-managed'
                        ? `Provider DNSSEC evidence recorded ${timeAgo(sync.dnssec.verifiedAt)}`
                        : 'Record provider-managed DNSSEC evidence'}
                    </summary>
                    <div className="mt-2 space-y-2">
                      <p>
                        API-pushed providers sign independently. Paste the exact DS records that
                        this provider says to publish at the registrar, one per line.
                      </p>
                      <Textarea
                        aria-label={`${provider.label} DS records`}
                        rows={3}
                        value={providerDs[provider.id] ?? sync.dnssec?.dsRecords.join('\n') ?? ''}
                        onChange={(event) =>
                          setProviderDs((current) => ({
                            ...current,
                            [provider.id]: event.target.value,
                          }))
                        }
                      />
                      <Button
                        variant="secondary"
                        loading={
                          configureDnssec.isPending &&
                          configureDnssec.variables.providerId === provider.id
                        }
                        disabled={
                          (
                            providerDs[provider.id] ??
                            sync.dnssec?.dsRecords.join('\n') ??
                            ''
                          ).trim() === ''
                        }
                        onClick={() =>
                          configureDnssec.mutate({
                            providerId: provider.id,
                            dsRecords: (
                              providerDs[provider.id] ??
                              sync.dnssec?.dsRecords.join('\n') ??
                              ''
                            )
                              .split('\n')
                              .map((record) => record.trim())
                              .filter(Boolean),
                          })
                        }
                      >
                        Record provider DNSSEC
                      </Button>
                    </div>
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ZoneDetail({ zoneId, featureEnabled }: { zoneId: string; featureEnabled: boolean }) {
  const queryClient = useQueryClient();
  const [addRecord, setAddRecord] = useState(false);
  const [editRecord, setEditRecord] = useState<DnsRecordDto | null>(null);
  const [resyncResult, setResyncResult] = useState<DnsTemplateSyncResultDto | null>(null);

  const detail = useQuery({
    queryKey: ['dns', 'zone', zoneId],
    queryFn: () => api.get<DnsZoneDetailDto>(`/api/dns/zones/${zoneId}`),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['dns'] });
  const removeRecord = useMutation({
    mutationFn: (id: string) => api.delete(`/api/dns/records/${id}`),
    onSuccess: invalidate,
  });
  const resync = useMutation({
    mutationFn: () =>
      api.post<DnsTemplateSyncResultDto>(`/api/dns/zones/${zoneId}/template-resync`),
    onSuccess: (result) => {
      setResyncResult(result);
      invalidate();
    },
  });

  if (detail.isPending) return <Spinner />;
  if (detail.error) {
    return (
      <div className="px-5 py-3">
        <ErrorNote error={detail.error} />
      </div>
    );
  }
  const zone = detail.data;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 pb-1 pt-3">
        <h4 className="flex items-center gap-2 text-sm font-semibold">
          Records
          {zone.serial !== null && (
            <span className="font-mono text-xs font-normal text-slate-500 dark:text-slate-400">
              serial {zone.serial}
            </span>
          )}
        </h4>
        {featureEnabled && (
          <div className="flex items-center gap-1">
            {zone.templateId && (
              <Button variant="ghost" loading={resync.isPending} onClick={() => resync.mutate()}>
                <RefreshCw className="size-4" /> Re-sync template
              </Button>
            )}
            <Button variant="ghost" onClick={() => setAddRecord(true)}>
              <Plus className="size-4" /> Add record
            </Button>
          </div>
        )}
      </div>
      {(removeRecord.error ?? resync.error) != null && (
        <div className="px-5 py-2">
          <ErrorNote error={removeRecord.error ?? resync.error} />
        </div>
      )}
      {resyncResult && (
        <p className="px-5 pb-2 text-xs text-slate-500 dark:text-slate-400">
          Template re-applied: {resyncResult.added} added · {resyncResult.updated} updated ·{' '}
          {resyncResult.removed} removed · {resyncResult.preserved} preserved (user-modified)
        </p>
      )}
      <RecordsTable
        detail={zone}
        featureEnabled={featureEnabled}
        onEdit={setEditRecord}
        onDelete={(record) => {
          if (window.confirm(`Delete ${record.type} record for ${record.fqdn}?`)) {
            removeRecord.mutate(record.id);
          }
        }}
      />
      <DnssecBlock zoneId={zone.id} featureEnabled={featureEnabled} />
      <ProviderSyncBlock zoneId={zone.id} syncs={zone.syncs} featureEnabled={featureEnabled} />
      <Modal
        title={`New record in ${zone.name}`}
        open={addRecord}
        onClose={() => setAddRecord(false)}
      >
        <RecordForm zoneId={zone.id} onDone={() => setAddRecord(false)} />
      </Modal>
      <Modal
        title={editRecord ? `Edit ${editRecord.fqdn} ${editRecord.type}` : 'Edit record'}
        open={editRecord !== null}
        onClose={() => setEditRecord(null)}
      >
        {editRecord && (
          <RecordForm zoneId={zone.id} existing={editRecord} onDone={() => setEditRecord(null)} />
        )}
      </Modal>
    </div>
  );
}

// ── Zone section ────────────────────────────────────────────────────

function ZoneSection({
  zone,
  featureEnabled,
  isAdmin,
}: {
  zone: DnsZoneDto;
  featureEnabled: boolean;
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editIp, setEditIp] = useState(false);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['dns'] });
  const restore = useMutation({
    mutationFn: () => api.post(`/api/dns/zones/${zone.id}/restore`),
    onSuccess: invalidate,
  });
  const removeZone = useMutation({
    mutationFn: () => api.delete(`/api/dns/zones/${zone.id}`, { confirmName: zone.name }),
    onSuccess: invalidate,
  });
  const mutationError = restore.error ?? removeZone.error;

  return (
    <div className="border-t border-slate-200 dark:border-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-5 py-3 dark:bg-slate-800/40">
        <button
          className="flex flex-wrap items-center gap-2 text-left text-sm"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          {open ? (
            <ChevronDown className="size-4 text-slate-400" aria-hidden />
          ) : (
            <ChevronRight className="size-4 text-slate-400" aria-hidden />
          )}
          <Globe className="size-4 text-slate-400" aria-hidden />
          <span className="font-medium">{zone.name}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {zone.recordCount} record{zone.recordCount === 1 ? '' : 's'}
          </span>
          {zone.dnssec && <Badge tone="green">dnssec</Badge>}
          {zone.orphanedAt && <Badge tone="yellow">missing on server</Badge>}
          {zone.serial !== null && (
            <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
              serial {zone.serial}
            </span>
          )}
        </button>
        <div className="flex items-center gap-1">
          {featureEnabled && zone.orphanedAt && (
            <Button
              variant="secondary"
              loading={restore.isPending}
              onClick={() => restore.mutate()}
            >
              <RefreshCw className="size-4" /> Restore
            </Button>
          )}
          {featureEnabled && (
            <Button
              variant="ghost"
              aria-label={`Edit zone ${zone.name}`}
              onClick={() => setEditIp(true)}
            >
              <Pencil className="size-4" />
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="ghost"
              className="hover:!text-red-600"
              aria-label={`Delete zone ${zone.name}`}
              loading={removeZone.isPending}
              onClick={() => {
                if (
                  window.prompt(
                    `Type ${zone.name}to delete it from the primary. External provider copies remain.`,
                  ) === zone.name
                ) {
                  removeZone.mutate();
                }
              }}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </div>
      </div>
      {mutationError != null && (
        <div className="px-5 py-2">
          <ErrorNote error={mutationError} />
        </div>
      )}
      {open && <ZoneDetail zoneId={zone.id} featureEnabled={featureEnabled} />}
      <Modal title={`Edit ${zone.name}`} open={editIp} onClose={() => setEditIp(false)}>
        <ZoneIpForm zone={zone} onDone={() => setEditIp(false)} />
      </Modal>
    </div>
  );
}

// ── Subscription detail: DNS panel ──────────────────────────────────

export function SubscriptionDnsPanel({ subscriptionId }: { subscriptionId: string }) {
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';
  const [addZone, setAddZone] = useState(false);

  const dns = useQuery({
    queryKey: ['dns', 'subscription', subscriptionId],
    queryFn: () => api.get<SubscriptionDnsDto>(`/api/dns/subscriptions/${subscriptionId}`),
    retry: false,
  });

  // Module disabled — hide the card entirely (matches the other panels).
  if (dns.error) return null;
  if (dns.isPending) {
    return (
      <Card>
        <CardHeader title="DNS" />
        <Spinner />
      </Card>
    );
  }
  const data = dns.data;
  if (!data.featureEnabled && data.zones.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="DNS"
        description="Authoritative zones on the panel-managed PowerDNS server"
        actions={
          !data.featureEnabled ? (
            <Badge tone="gray">not in plan</Badge>
          ) : data.configured ? (
            <Button onClick={() => setAddZone(true)}>
              <Plus className="size-4" /> Add zone
            </Button>
          ) : undefined
        }
      />
      {!data.configured ? (
        <EmptyState
          icon={<Globe className="size-10" />}
          title="DNS server not configured"
          description="An administrator connects the PowerDNS server under DNS."
          action={
            isAdmin ? (
              <Link to="/dns">
                <Button variant="secondary">Open DNS settings</Button>
              </Link>
            ) : undefined
          }
        />
      ) : data.zones.length === 0 ? (
        <EmptyState
          icon={<Globe className="size-10" />}
          title="No DNS zones"
          description={
            data.featureEnabled
              ? 'Add a zone to start managing records.'
              : 'The plan does not include DNS hosting.'
          }
        />
      ) : (
        data.zones.map((zone) => (
          <ZoneSection
            key={zone.id}
            zone={zone}
            featureEnabled={data.featureEnabled}
            isAdmin={isAdmin}
          />
        ))
      )}
      <Modal title="Add DNS zone" open={addZone} onClose={() => setAddZone(false)}>
        <ZoneForm subscriptionId={subscriptionId} onDone={() => setAddZone(false)} />
      </Modal>
    </Card>
  );
}

export function DnsDomainSettings({ zoneId }: { zoneId: string }) {
  const me = useMe();
  const qc = useQueryClient();
  const [confirmation, setConfirmation] = useState('');
  const detail = useQuery({
    queryKey: ['dns', 'zone', zoneId],
    queryFn: () => api.get<DnsZoneDetailDto>(`/api/dns/zones/${zoneId}`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['dns'] });
  const resync = useMutation({
    mutationFn: () => api.post(`/api/dns/zones/${zoneId}/template-resync`, {}),
    onSuccess: refresh,
  });
  const restore = useMutation({
    mutationFn: () => api.post(`/api/dns/zones/${zoneId}/restore`, {}),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/dns/zones/${zoneId}`, { confirmName: confirmation }),
    onSuccess: () => {
      void refresh();
      window.location.assign('/dns');
    },
  });
  if (detail.isPending) return <Spinner />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  const zone = detail.data;
  return (
    <Card>
      <CardHeader title="Domain settings" />
      <div className="space-y-4 px-5 pb-4">
        <ZoneIpForm zone={zone} onDone={() => void refresh()} />
        {zone.templateId && (
          <Button variant="secondary" loading={resync.isPending} onClick={() => resync.mutate()}>
            Re-sync template
          </Button>
        )}
        {zone.orphanedAt && (
          <Button loading={restore.isPending} onClick={() => restore.mutate()}>
            Restore primary zone
          </Button>
        )}
        {(resync.error ?? restore.error ?? remove.error) && (
          <ErrorNote error={resync.error ?? restore.error ?? remove.error} />
        )}
        <DnssecBlock zoneId={zoneId} featureEnabled />
        <ProviderSyncBlock zoneId={zoneId} syncs={zone.syncs} featureEnabled />
        {me.data?.role === 'admin' && (
          <div className="space-y-2 border-t pt-4">
            <p>
              Deleting this zone removes it from the primary DNS server. External provider copies
              remain for the operator to manage.
            </p>
            <Label htmlFor="dns-delete-confirm">Type {zone.name} to delete</Label>
            <Input
              id="dns-delete-confirm"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
            <Button
              variant="danger"
              disabled={confirmation !== zone.name}
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Delete zone
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
